import { useState } from "react";
import { Trash2, Wrench } from "lucide-react";
import { foApi } from "@/webapp/fleet-owners/api";
import { errorText } from "@/lib/api";
import { Drawer, Tag, Button, Label, Field, Select, Notice, ExpiryCell, money } from "@/webapp/fleet-owners/ui";

const KINDS = ["inspection", "maintenance", "repair", "tires", "oil"];
const VTONE = { active: "ok", maintenance: "warn", out_of_service: "bad", retired: "mute" };

function Facts({ v, driver }) {
  const rows = [["Plate", v.plate || "—", true], ["VIN", v.vin || "—", true], ["Vehicle", [v.year, v.make, v.model].filter(Boolean).join(" ") || "—"], ["Class", v.vclass || "—"], ["Trailer", v.trailer || "—"], ["Assigned driver", driver?.name || "—"]];
  return (
    <dl className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm" data-testid="fo-vd-facts">
      {rows.map(([k, val, mono]) => <div key={k}><dt className="text-xs text-slate-500">{k}</dt><dd className={`${mono ? "font-mono tracking-wide" : ""} font-semibold text-[#0A234A]`}>{val}</dd></div>)}
      <div><dt className="text-xs text-slate-500">Registration</dt><dd><ExpiryCell date={v.reg_expiry} /></dd></div>
      <div><dt className="text-xs text-slate-500">Insurance</dt><dd><ExpiryCell date={v.insurance_expiry} /></dd></div>
      <div><dt className="text-xs text-slate-500">Inspection due</dt><dd><ExpiryCell date={v.inspection_due} warn={14} /></dd></div>
    </dl>
  );
}

function ServiceForm({ v, reload }) {
  const blank = { kind: "inspection", date: new Date().toISOString().slice(0, 10), odometer: "", cost: "", next_due: "", notes: "" };
  const [f, setF] = useState(blank);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const add = async () => { setBusy(true); setErr(""); try { await foApi.addService(v.id, f); setF(blank); await reload(); } catch (e) { setErr(errorText(e)); } setBusy(false); };
  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-slate-50/60 p-4" data-testid="fo-vd-service-form">
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Type</Label><Select data-testid="fo-vd-service-kind" value={f.kind} onChange={set("kind")}>{KINDS.map((k) => <option key={k} value={k}>{k}</option>)}</Select></div>
        <div><Label>Date</Label><Field data-testid="fo-vd-service-date" type="date" value={f.date} onChange={set("date")} /></div>
        <div><Label>Odometer</Label><Field data-testid="fo-vd-service-odometer" type="number" min={0} value={f.odometer} onChange={set("odometer")} placeholder="km" /></div>
        <div><Label>Cost</Label><Field data-testid="fo-vd-service-cost" type="number" min={0} step="0.01" value={f.cost} onChange={set("cost")} placeholder="$" /></div>
        <div className="col-span-2"><Label>Next due</Label><Field data-testid="fo-vd-service-next" type="date" value={f.next_due} onChange={set("next_due")} /></div>
        <div className="col-span-2"><Label>Notes</Label><Field data-testid="fo-vd-service-notes" value={f.notes} onChange={set("notes")} maxLength={400} placeholder="What was done" /></div>
      </div>
      {err && <Notice testid="fo-vd-service-error">{err}</Notice>}
      <Button variant="navy" testid="fo-vd-service-add" onClick={add} disabled={busy}><Wrench className="h-4 w-4" />Log service</Button>
    </div>
  );
}

function ServiceLog({ entries, reload }) {
  const remove = async (s) => { if (window.confirm("Delete this service entry?")) { await foApi.removeService(s.id); await reload(); } };
  if (!entries.length) return <p className="py-4 text-sm text-slate-400" data-testid="fo-vd-service-empty">No service history yet.</p>;
  return (
    <ol className="relative space-y-4 border-l border-slate-200 pl-5" data-testid="fo-vd-service-log">
      {entries.map((s) => (
        <li key={s.id} className="relative" data-testid={`fo-vd-service-${s.id}`}>
          <span className="absolute -left-[25px] top-1.5 h-2 w-2 rounded-full bg-[#FF5722]" />
          <div className="flex items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold capitalize text-[#0A234A]">{s.kind} <span className="font-mono text-xs font-normal text-slate-500">{s.date}</span></p>
              <p className="text-xs text-slate-500">{[s.odometer != null && `${Number(s.odometer).toLocaleString()} km`, s.cost != null && money(s.cost), s.next_due && `next due ${s.next_due}`].filter(Boolean).join(" · ") || "—"}</p>
              {s.notes && <p className="mt-0.5 text-xs text-slate-600">{s.notes}</p>}
            </div>
            <button data-testid={`fo-vd-service-delete-${s.id}`} onClick={() => remove(s)} className="rounded p-1 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
          </div>
        </li>
      ))}
    </ol>
  );
}

export default function VehicleDrawer({ vehicle: v, data, reload, onClose, onEdit }) {
  if (!v) return null;
  const driver = data.drivers.find((d) => d.vehicle_id === v.id);
  const entries = data.service.filter((s) => s.vehicle_id === v.id);
  const remove = async () => { if (window.confirm(`Remove ${v.unit}? Its service log is deleted too.`)) { await foApi.removeVehicle(v.id); onClose(); await reload(); } };
  return (
    <Drawer open testid="fo-vehicle-drawer" onClose={onClose} title={<span className="flex items-center gap-3">{v.unit}<Tag tone={VTONE[v.status]}>{String(v.status).replace(/_/g, " ")}</Tag></span>}>
      <Facts v={v} driver={driver} />
      <div className="mt-5 flex gap-2">
        <Button testid="fo-vd-edit" onClick={() => onEdit(v)}>Edit</Button>
        <Button variant="danger" testid="fo-vd-remove" onClick={remove}>Remove</Button>
      </div>
      <h3 className="mb-3 mt-8 font-display text-sm font-semibold text-[#0A234A]">Service log</h3>
      <ServiceForm v={v} reload={reload} />
      <div className="mt-5"><ServiceLog entries={entries} reload={reload} /></div>
    </Drawer>
  );
}
